/**
 * Dark charcoal tooltip style used across Edit Account UI
 * (matches MUI default Tooltip look: dark bg, white text, arrow).
 */
import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

export const EDIT_ACCOUNT_TOOLTIP_SX = {
  bgcolor: "rgba(97, 97, 97, 0.92)",
  color: "#fff",
  fontSize: "0.75rem",
  fontWeight: 400,
  lineHeight: 1.4,
  maxWidth: 320,
  px: 1.5,
  py: 1,
  borderRadius: "4px",
  boxShadow: "none",
  "& .MuiTooltip-arrow": {
    color: "rgba(97, 97, 97, 0.92)",
  },
};

export const EDIT_ACCOUNT_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: EDIT_ACCOUNT_TOOLTIP_SX,
  },
};

/** Spread on Tooltip when not inside AccountDetailThemeProvider. */
export const APP_TOOLTIP_DEFAULT_PROPS = {
  arrow: true,
  slotProps: EDIT_ACCOUNT_TOOLTIP_SLOT_PROPS,
};

export const editAccountTooltipThemeOverrides = {
  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: {
        backgroundColor: "rgba(97, 97, 97, 0.92)",
        color: "#fff",
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: 1.4,
        maxWidth: 320,
        padding: "8px 12px",
        borderRadius: "4px",
        boxShadow: "none",
      },
      arrow: {
        color: "rgba(97, 97, 97, 0.92)",
      },
    },
  },
};

const TRUNCATED_TEXT_SX = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** MUI tooltip with shared dark styling (replaces native HTML title tooltips). */
export const AccountDetailTooltip = ({
  title,
  children,
  placement = "top",
  disableHoverListener = false,
}) => {
  if (!title) return children;

  return (
    <Tooltip
      {...APP_TOOLTIP_DEFAULT_PROPS}
      title={title}
      placement={placement}
      disableHoverListener={disableHoverListener}
    >
      <Box
        component="span"
        sx={{
          display: "inline-block",
          maxWidth: "100%",
          minWidth: 0,
          verticalAlign: "bottom",
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

/** Ellipsis cell text with dark MUI tooltip on hover. */
export const TruncatedTooltipText = ({ title, sx, children, ...typographyProps }) => {
  const tooltipTitle = title ?? children;
  const text = (
    <Typography sx={{ ...TRUNCATED_TEXT_SX, ...sx }} {...typographyProps}>
      {children}
    </Typography>
  );

  if (!tooltipTitle) return text;

  return (
    <AccountDetailTooltip title={String(tooltipTitle)}>{text}</AccountDetailTooltip>
  );
};
